import { IVatCode, ISingleCompany, ISingleClient } from '@typings'
import { isEUCountry } from '@helpers/country/euCountries.helper'

/**
 * Filter available VAT codes based on company and client tax status
 *
 * Dutch VAT Rules:
 * 1. If company is NOT tax payable: NO VAT can be charged at all (return empty array)
 * 2. If company uses KOR (Kleine Ondernemingsregeling): ONLY 0% VAT allowed
 * 3. If company IS tax payable but client has NO VAT number: exclude "BTW Verlegd"
 * 4. ICL codes (Intracommunautaire levering): Both parties must have VAT numbers AND client in EU
 * 5. Export codes: Client must be outside the EU
 * 6. If company IS tax payable AND client has VAT number: remaining codes allowed
 *
 * @param vatCodes - All available VAT codes
 * @param company - Company entity
 * @param client - Client entity (optional)
 * @returns Filtered VAT codes that are allowed based on tax rules
 */
export const filterAvailableVatCodes = (
  vatCodes: IVatCode[],
  company: ISingleCompany | null | undefined,
  client: ISingleClient | null | undefined
): IVatCode[] => {
  if (!company || !vatCodes || vatCodes.length === 0) {
    return vatCodes || []
  }

  // Rule 1: If company is not tax payable, NO VAT can be charged
  if (!company.tax_payable) {
    return []
  }

  // Rule 2: If company uses KOR (Kleine Ondernemingsregeling), ONLY 0% VAT allowed
  if (company.uses_kor) {
    return vatCodes.filter(code => code.percentage === 0)
  }

  // Check if both parties have VAT numbers
  const clientHasVatNumber = client && client.tax && client.tax.trim().length > 0
  const companyHasVatNumber = company.tax && company.tax.trim().length > 0
  const bothHaveVatNumbers = clientHasVatNumber && companyHasVatNumber

  // Check if client is in EU (excluding Netherlands)
  const clientCountryCode = client?.country?.code
  const clientIsInEU = clientCountryCode && isEUCountry(clientCountryCode) && clientCountryCode !== 'NL'
  const clientIsOutsideEU = clientCountryCode && !isEUCountry(clientCountryCode)

  // Filter out codes that are not applicable based on rules
  return vatCodes.filter(code => {
    // Rule 3: "BTW Verlegd" requires both parties to have VAT numbers
    if (code.name === 'BTW Verlegd' && !bothHaveVatNumbers) {
      return false
    }

    // Rule 4: ICL codes require both parties to have VAT numbers AND client in EU (not NL)
    if (
      (code.name === 'ICL - Goederen' || code.name === 'ICL - Diensten') &&
      (!bothHaveVatNumbers || !clientIsInEU)
    ) {
      return false
    }

    // Rule 5: Export codes require client to be outside EU
    if ((code.name === 'Export Goederen' || code.name === 'Export Diensten') && !clientIsOutsideEU) {
      return false
    }

    // All other codes are allowed (Rule 6)
    return true
  })
}
